/**
 * API Configuration — GenesisBox
 */

import { REQUEST_HEADERS } from '@config/constants';
import { config } from '@config/environment';
import { getFirebaseAuth } from '@config/firebase';
import {
  clearActiveConnectionId,
  clearActiveOrgId,
  getActiveConnectionId,
  getActiveOrgId,
} from '@services/storage/savedConnectionsStorage';

const TOKEN_KEY = 'genesisbox:jwt';
const USER_KEY = 'genesisbox:user';
const ORG_KEY = 'genesisbox:org';

export const AUTH_SESSION_EXPIRED_EVENT = 'genesisbox:auth-session-expired';
export const AUTH_SESSION_NOTICE_KEY = 'genesisbox:authNotice';
export const AUTH_SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

export interface StorageScope {
  orgId: string;
  connectionId: string;
}

type ApiErrorBody = {
  error?: string;
  details?: string;
};

export function getApiBase(): string {
  return config.apiBaseUrl.replace(/\/+$/, '');
}

export async function getAuthHeaders(
  options: {
    includeActiveConnection?: boolean;
    includeActiveOrg?: boolean;
    storageScope?: StorageScope | null;
  } = {},
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  let token = localStorage.getItem(TOKEN_KEY);
  const includeActiveConnection = options.includeActiveConnection ?? true;
  const includeActiveOrg = options.includeActiveOrg ?? true;

  if (token?.startsWith('basic:')) {
    headers.Authorization = `Basic ${token.slice(6)}`;
  } else if (token) {
    try {
      const currentUser = getFirebaseAuth().currentUser;
      if (currentUser) {
        token = await currentUser.getIdToken();
        localStorage.setItem(TOKEN_KEY, token);
      }
    } catch {
      // Firebase may still be restoring its session. The stored ID token
      // remains the best available credential for this request.
    }
    headers.Authorization = `Bearer ${token}`;
  }

  const activeOrgId = includeActiveOrg
    ? (options.storageScope?.orgId || getActiveOrgId())
    : null;
  if (activeOrgId) {
    headers[REQUEST_HEADERS.activeOrgId] = activeOrgId;
  }

  const activeConnectionId = includeActiveConnection
    ? (options.storageScope?.connectionId || getActiveConnectionId(activeOrgId))
    : null;
  if (activeConnectionId) {
    headers[REQUEST_HEADERS.activeConnectionId] = activeConnectionId;
  }

  return headers;
}

function getStoredToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

function hasBearerSession(): boolean {
  const token = getStoredToken();
  return !!token && !token.startsWith('basic:');
}

function isSessionExpiredResponse(res: Response, body: ApiErrorBody | null): boolean {
  if (res.status !== 401 || !hasBearerSession()) return false;
  const message = `${body?.error || ''} ${body?.details || ''}`.toLowerCase();
  return (
    message.includes('invalid or expired token')
    || message.includes('invalid token')
    || message.includes('not authenticated')
  );
}

export function expireAuthSession(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORG_KEY);
    localStorage.setItem(AUTH_SESSION_NOTICE_KEY, AUTH_SESSION_EXPIRED_MESSAGE);
  }

  clearActiveConnectionId();
  clearActiveOrgId();

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
  }
}

export async function readJsonResponse<T = any>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    if (isSessionExpiredResponse(res, body)) {
      expireAuthSession();
      throw new Error(AUTH_SESSION_EXPIRED_MESSAGE);
    }

    const message = body?.error || body?.details || `HTTP ${res.status}`;
    throw new Error(message);
  }

  return body;
}

export function buildUrl(path: string, params?: Record<string, string>): string {
  const url = new URL(path, getApiBase() + '/');
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') url.searchParams.set(k, v);
    });
  }
  return url.toString();
}
